import { FlexRow, MaterialIcon, StyledButton, useModal } from '@gorila-shared-ui/components';
import { ChevronUp } from 'baseui/icon';
import { StatefulMenu } from 'baseui/menu';
import { Popover } from 'baseui/popover';
import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { REQUEST_VIDEO_PAGE, STREAM_PAGE } from '../../../constants/app';
import { useStyles } from '../../../hooks/useStyles';
import { workspaceState } from '../../../storage/app';
import { CurrentState } from '../../../types/currentState';
import { StreamingMenuActions } from '../../../types/streaming';
import { hasEnabledCameraFeatures } from '../../../utils/currentStates';
import StreamingCaptureModal from './modal/StreamingCaptureModal';

type Props = {
  currentState: CurrentState;
};
export function StreamingMenu({ currentState }: Readonly<Props>) {
  const { theme, classes } = useStyles();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const { showModal: showCaptureModal, closeModal: closeCaptureModal, openModal: openCaptureModal } = useModal();
  const enabledCameraFeatures = hasEnabledCameraFeatures(currentState);
  const iconColor = enabledCameraFeatures ? theme.colors.contentPrimary : theme.colors.contentStateDisabled;
  const navigate = useNavigate();
  const workspace = useRecoilValue(workspaceState);

  const toggleMenu = () => {
    setMenuIsOpen((prev) => !prev);
  };

  const closeMenu = () => {
    setMenuIsOpen(false);
  };

  const onItemSelect = (item: any) => {
    if (item.id === StreamingMenuActions.live) {
      const route = generatePath(STREAM_PAGE.route, { id: workspace?._id, ident: currentState.ident });
      navigate(route, { replace: false });
    }
    if (item.id === StreamingMenuActions.capture) {
      openCaptureModal();
    }
    if (item.id === StreamingMenuActions.video) {
      const route = generatePath(REQUEST_VIDEO_PAGE.route, { id: workspace?._id, ident: currentState.ident });
      navigate(route, { replace: false });
    }
    closeMenu();
  };

  return (
    <>
      <Popover
        isOpen={menuIsOpen}
        content={() => (
          <StatefulMenu
            onItemSelect={({ item }) => onItemSelect(item)}
            items={[
              {
                id: StreamingMenuActions.video,
                label: (
                  <FlexRow
                    classNames={classes.centeredStart}
                    gap={theme.sizing.scale400}
                  >
                    <MaterialIcon
                      name="videocam"
                      size="mini"
                    />
                    Solicitar video
                  </FlexRow>
                ),
              },
              {
                id: StreamingMenuActions.live,
                disabled: !enabledCameraFeatures,
                label: (
                  <FlexRow
                    classNames={classes.centeredStart}
                    gap={theme.sizing.scale400}
                  >
                    <MaterialIcon
                      name="live"
                      size="mini"
                      color={iconColor}
                    />
                    Ver en vivo
                  </FlexRow>
                ),
              },
              {
                id: StreamingMenuActions.capture,
                disabled: !enabledCameraFeatures,
                label: (
                  <FlexRow
                    classNames={classes.centeredStart}
                    gap={theme.sizing.scale400}
                  >
                    <MaterialIcon
                      name="image"
                      size="mini"
                      color={iconColor}
                    />
                    Foto en tiempo real
                  </FlexRow>
                ),
              },
            ]}
          />
        )}
        placement={'bottomRight'}
        triggerType="click"
        onClick={toggleMenu}
        onClickOutside={closeMenu}
      >
        <span style={{ height: '24px' }}>
          <StreamButton
            isOpen={menuIsOpen}
            isOn
          />
        </span>
      </Popover>
      <StreamingCaptureModal
        asset={currentState.asset}
        isOpen={showCaptureModal}
        onClose={closeCaptureModal}
      />
    </>
  );
}

const StreamButton = ({ isOpen, isOn = false }: { isOpen: boolean; isOn?: boolean }) => {
  const { theme } = useStyles();
  return (
    <StyledButton
      size="compact"
      kind="tertiary"
      disabled={!isOn}
      overrides={{
        BaseButton: {
          style: {
            height: '24px',
            overflow: 'hidden',
            paddingLeft: theme.sizing.scale400,
            paddingRight: theme.sizing.scale300,
            paddingTop: 0,
            paddingBottom: 0,
            borderTopLeftRadius: theme.sizing.scale100,
            borderTopRightRadius: theme.sizing.scale100,
            borderBottomLeftRadius: theme.sizing.scale100,
            borderBottomRightRadius: theme.sizing.scale100,
          },
        },
      }}
    >
      <FlexRow
        centered
        gap={theme.sizing.scale100}
      >
        <MaterialIcon
          name="nest_cam_outdoor"
          size="mini"
          color={isOn ? theme.colors.contentPrimary : theme.colors.contentStateDisabled}
        />
        <ChevronUp
          title={''}
          overrides={{
            Svg: {
              style: {
                transition: 'transform 0.2s ease-in-out',
                transform: !isOpen ? 'rotate(180deg)' : undefined,
              },
            },
          }}
        />
      </FlexRow>
    </StyledButton>
  );
};
